import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {canEditFolioItems} from "crqlar-react-common/dist/utils/folio.util";
import {useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useRef} from "react";
import {useQuery, useQueryClient} from "react-query";
import {DigitalSigningService} from "../../../../digitalSigning/digitalSigning.service";
import {DIGITAL_SIGNING_STALE_TIME, digitalSigningFolioKey} from "../../../../digitalSigning/digitalSigningQueries";
import {isLiveDigitalSigning} from "../../../../digitalSigning/share/digitalSigningFlow.util";
import {
    cachedListFolio,
    fetchFolioItems,
    FOLIO_DIALOG_FOLIO_KEY,
    FOLIO_ITEMS_STALE_TIME,
    folioItemsKey,
    invalidateFolioLists
} from "./folioQueryKeys";
import {useFolioDraft} from "./useFolioDraft";

const NO_IDS = new Set<string>();

interface Options {
    open: boolean
    /** The folio shown; undefined while the dialog has none (the list view, a folio being prepared). */
    folioId?: string
    reservation?: Reservation
    /** The reservation's service: a package's lines after the first stay hidden. */
    service?: Service
    onChanged?: (folio: Folio) => void
}

/**
 * The folio the dialog shows and its lines, edited as a local draft. Kept by the dialog frame, so a
 * switch of view or folio can save the draft first.
 */
export const useFolioSession = ({open, folioId, reservation, service, onChanged}: Options) => {
    const queryClient = useQueryClient();
    const readOnlyUser = useReadOnly();

    // Read fresh on every opening; actions put the folio they return here. Meanwhile the folio as the
    // list has it shows (the value itself, never a memoized function: the query would keep the previous
    // folio's placeholder)
    const folioQuery = useQuery(
        [FOLIO_DIALOG_FOLIO_KEY, folioId],
        () => FolioService.getById(folioId!),
        {enabled: open && !!folioId, refetchOnWindowFocus: false, cacheTime: 0, placeholderData: cachedListFolio(queryClient, folioId)}
    );
    const folio = folioId ? folioQuery.data : undefined;
    const setFolio = (next: Folio) => queryClient.setQueryData([FOLIO_DIALOG_FOLIO_KEY, next.id], next);

    // Every change is reported, and every folio list reads again - here and in the legacy dialogs
    const reportChange = (changed: Folio) => {
        invalidateFolioLists(queryClient);
        onChanged?.(changed);
    };

    // The folio's latest signing - the folio view's own query (same key, and its polling keeps
    // it current), read here as well so the lines lock on a live or signed link before the view has
    // rendered, e.g. right after a switch. Only a folio of a reservation can have one.
    const signingQuery = useQuery(
        digitalSigningFolioKey(folioId),
        () => DigitalSigningService.latestForFolio(folioId!),
        {enabled: open && !!folioId && !!reservation?.id, refetchOnWindowFocus: false, staleTime: DIGITAL_SIGNING_STALE_TIME}
    );
    const latestSigning = signingQuery.data;
    const digitalSigning = {status: latestSigning?.status, id: latestSigning?.id};
    const guestSigned = digitalSigning.status === 'SIGNED';
    const guestLinkOpen = isLiveDigitalSigning(digitalSigning.status);
    // A confirm that didn't finish holds them too: the next Confirm and charge finishes it on these lines
    const confirmUnfinished = digitalSigning.status === 'CONFIRMED' && !!latestSigning?.confirmIncomplete;
    // A failed read doesn't hold the lines back: create and sign check the folio on the server anyway
    const signingKnown = !reservation?.id || signingQuery.isSuccess || signingQuery.isError;
    // The folio view reads it again at once, instead of at its next poll
    const refreshDigitalSigning = () => queryClient.invalidateQueries(digitalSigningFolioKey(folio?.id));

    // Read beside the folio, not after it; the dialog reads them ahead for the reservation's folios.
    // Keyed by the folio, so a folio never gets another's lines - a switch shows the new folio's cached
    // lines or none until they arrive
    const itemsQuery = useQuery(
        folioItemsKey(folioId),
        () => fetchFolioItems(folioId!),
        {enabled: open && !!folioId, refetchOnWindowFocus: false, staleTime: FOLIO_ITEMS_STALE_TIME}
    );

    // A package's lines after the first are its parts: hidden, as first seen per folio (lines staff add
    // later stay visible)
    const packageChildren = useRef(new Map<string, Set<string>>());
    if (folio?.id && itemsQuery.data && !packageChildren.current.has(folio.id)) {
        const items = itemsQuery.data;
        packageChildren.current.set(folio.id, service?.isPackage && items.length > 1
            ? new Set(items.slice(1).map(item => item.id).filter((id): id is string => !!id))
            : NO_IDS);
    }
    const hiddenIds = (folio?.id && packageChildren.current.get(folio.id)) || NO_IDS;

    // Lines change only on a draft - and not while a guest link is live or signed: the guest confirms
    // exactly what is saved
    const linesEditable = !!folio?.id && folio.status === FolioStatus.DRAFT && canEditFolioItems(folio)
        && !guestSigned && !guestLinkOpen && !confirmUnfinished && !readOnlyUser;
    const draft = useFolioDraft({
        folioId: folio?.id,
        // The lines appear once, already locked or editable - never editable for a moment on a signed folio
        serverItems: signingKnown ? itemsQuery.data : undefined,
        editable: linesEditable,
        refetch: async () => (await itemsQuery.refetch()).data,
        onSaved: () => invalidateFolioLists(queryClient),
    });

    const services = useServicesForSector(reservation?.sectorId ?? service?.sectorIds?.[0]);

    // First what leaving the folio does - its draft is saved; a failed save stays, with its error above
    // the lines, and nothing is read -, then the folio, its lines and its signing are read
    // again. The signing's query is enabled only for a folio of a reservation, and only an
    // enabled one reads
    const refresh = async (): Promise<boolean> => {
        if (!folioId || !(await draft.persist())) return false;
        const [folioRead, itemsRead] = await Promise.all([
            folioQuery.refetch(),
            itemsQuery.refetch(),
            queryClient.invalidateQueries(digitalSigningFolioKey(folioId)),
        ]);
        // A failed read doesn't reject; the header's Refresh says it didn't work
        return !folioRead.isError && !itemsRead.isError;
    };

    return {
        folio,
        folioLoading: !!folioId && folioQuery.isLoading,
        /** It couldn't be read, or it isn't there (any more). */
        folioError: !!folioId && !folio && (folioQuery.isError || folioQuery.isSuccess),
        refetchFolio: () => folioQuery.refetch(),
        setFolio,
        reportChange,
        refetchItems: () => itemsQuery.refetch(),
        /** Saves the draft, then reads the folio, its lines and its signing again; false when the save or a read failed. */
        refresh,
        /** The lines couldn't be read (after the query's retries). */
        linesError: !!folioId && itemsQuery.isError && !itemsQuery.data,
        draft,
        linesEditable,
        readOnlyUser,
        hiddenIds,
        /** The lines Finalize checks: package parts stay out. */
        visibleItems: draft.lines.filter(line => !line.item.id || !hiddenIds.has(line.item.id)).map(line => line.item),
        digitalSigning,
        refreshDigitalSigning,
        guestSigned,
        guestLinkOpen,
        services,
    };
};

export type FolioSession = ReturnType<typeof useFolioSession>;
